import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { translateOriginalContents } from '../../../../entities/post/api/translate';

import type { Post } from '@/entities/post/types';
import { Button } from '@/shared/components/shadcn/button';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { cn } from '@/shared/utils/cn';
import { CollapsibleBox, MutedBox, PostSectionTitle } from './post.section-components';

export const OriginalContents = ({ post }: { post: Post }) => {
  const { workspace } = useWorkspace();
  const [mode, setMode] = useState<'original' | 'translated'>('original');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContents, setTranslatedContents] = useState<{
    content: string;
    title?: string;
  } | null>(null);

  const contents =
    mode === 'translated'
      ? translatedContents
      : {
          title: post.extraction.title,
          content: post.extraction.content,
        };

  const handleTranslate = async () => {
    if (translatedContents) {
      setMode('translated');
    } else {
      setIsTranslating(true);
      const { content, title } = await translateOriginalContents(post._id, workspace.language);
      setTranslatedContents({
        content,
        title,
      });
      setIsTranslating(false);
      setMode('translated');
    }
  };

  if (!contents) return null;

  return (
    <MutedBox>
      <CollapsibleBox
        trigger={<PostSectionTitle>Original contents</PostSectionTitle>}
        actionNode={
          mode === 'original' ? (
            <Button variant="ghost" size="icon" onClick={handleTranslate}>
              {isTranslating && <Loader2 className="size-4 animate-spin" />}
              Translate
            </Button>
          ) : (
            <Button variant="ghost" size="icon" onClick={() => setMode('original')}>
              See original
            </Button>
          )
        }
        initialOpen
      >
        <div
          className={cn(
            'text-muted-foreground flex min-w-0 flex-col gap-4 pt-6 text-sm break-all transition-opacity duration-300',
            isTranslating && 'opacity-50',
          )}
        >
          {contents.title && <h3>{contents.title}</h3>}
          {contents.content}
        </div>
      </CollapsibleBox>
    </MutedBox>
  );
};
